import { Permissions, ActionType, FullPermissionKeys } from 'services/types/user'

export const PERMISSIONS_MAP = {
  company: {
    manage: {
      edit: 'EDIT_COMPANY',
      view: 'VIEW_COMPANY',
    },
  },
  branchManagement: {
    branch: {
      create: 'CREATE_BRANCH',
      delete: 'DELETE_BRANCH',
      manage: {
        deactivate: 'DEACTIVATE_BRANCH',
        edit: 'EDIT_BRANCH',
        view: 'VIEW_BRANCH',
      },
    },
    department: {
      create: 'CREATE_DEPARTMENT',
      delete: 'DELETE_DEPARTMENT',
      manage: {
        edit: 'EDIT_DEPARTMENT',
        view: 'VIEW_DEPARTMENT',
      },
    },
  },
  marketingManagement: {
    deleteData: 'DELETE_DATA',
    salesCampaign: {
      add: 'ADD_SALES_CAMPAIGN',
      manage: {
        edit: 'EDIT_SALES_CAMPAIGN',
        view: 'VIEW_SALES_CAMPAIGN',
      },
    },
    salesSource: {
      add: 'ADD_SALES_SOURCE',
      manage: {
        edit: 'EDIT_SALES_SOURCE',
        view: 'VIEW_SALES_SOURCE',
      },
    },
  },
  salesManagement: {
    workflow: {
      account: {
        assignOther: 'LEAD_OWNER_ASSIGN_LEAD',
        create: 'CREATE_ACCOUNT',
        manage: {
          edit: 'EDIT_OWN_ACCOUNT',
          view: 'VIEW_OWN_ACCOUNT',
        },
        manageOther: {
          edit: 'EDIT_OTHER_ACCOUNT',
          view: 'VIEW_OTHER_ACCOUNT',
        },
        merge: 'MERGE_ACCOUNT',
        activity: {
          delete: 'DELETE_ACTIVITY',
        },
      },
      data: {
        delete: 'DELETE_DATA',
        export: 'EXPORT_DATA',
        import: 'IMPORT_DATA',
      },
    },
    products: {
      add: 'ADD_PRODUCT',
      delete: 'DELETE_PRODUCT',
      manage: {
        edit: 'EDIT_PRODUCT',
        view: 'VIEW_PRODUCT',
      },
      priceTable: {
        create: 'CREATE_PRICE_TABLE',
        delete: 'DELETE_PRICE_TABLE',
        manage: {
          deactivate: 'DEACTIVATE_PRICE_TABLE',
          edit: 'EDIT_PRICE_TABLE',
          view: 'VIEW_PRICE_TABLE',
        },
      },
      priceEscalation: {
        canEdit: 'ALLOW_ESCALATION',
      },
      priceDiscount: {
        canEdit: 'ALLOW_DISCOUNT_CHANGE',
      },
    },
  },
  userManagement: {
    approveRegistration: 'APPROVE_USER_REGISTRATION',
    assignAdmin: 'ASSIGN_ADMINISTRATOR',
    assignPermission: 'ASSIGN_USER_PERMISSION',
    delete: 'DELETE_USER',
    resetPassword: 'RESET_USER_PASSWORD',
    manage: {
      deactivate: 'DEACTIVATE_USER',
      edit: 'EDIT_USER',
      view: 'VIEW_USER',
    },
  },
  externalApiManagement: {
    generateApiToken: 'GENERATE_EXTERNAL_API_TOKEN',
    manage: {
      view: 'VIEW_EXTERNAL_API',
      edit: 'EDIT_EXTERNAL_API',
    },
    pitchLibrary: {
      view: 'VIEW_PITCH_LIBRARY',
      edit: 'EDIT_PITCH_LIBRARY',
    },
  },
  permissions: {
    create: 'CREATE_PROFILE',
    delete: 'DELETE_PROFILE',
    manage: {
      edit: 'EDIT_PROFILE_PERMISSIONS',
      view: 'VIEW_PROFILE_PERMISSIONS',
    },
  },
  branchSettings: {
    manage: {
      view: 'VIEW_BRANCH_SETTINGS',
      edit: 'EDIT_BRANCH_SETTINGS',
    },
  },
}

export const PERMISSIONS: {
  [k in FullPermissionKeys]: {
    groupName: keyof Permissions
    actionKey: ActionType
  }
} = {
  // COMPANY
  VIEW_COMPANY: { groupName: 'company', actionKey: 'canView' },
  EDIT_COMPANY: { groupName: 'company', actionKey: 'canEdit' },

  //BRANCH
  CREATE_NEW_BRANCH: { groupName: 'branch', actionKey: 'canCreate' },
  VIEW_BRANCH: { groupName: 'branch', actionKey: 'canView' },
  EDIT_BRANCH: { groupName: 'branch', actionKey: 'canEdit' },
  DEACTIVATE_BRANCH: { groupName: 'branch', actionKey: 'canDeactivate' },
  DELETE_BRANCH: { groupName: 'branch', actionKey: 'canDelete' },

  // DEPARTMENT
  VIEW_DEPARTMENT: { groupName: 'department', actionKey: 'canView' },
  EDIT_DEPARTMENT: { groupName: 'department', actionKey: 'canEdit' },
  DELETE_DEPARTMENT: { groupName: 'department', actionKey: 'canDeactivate' },

  //USER MANAGEMENT
  VIEW_USER: { groupName: 'user', actionKey: 'canView' },
  EDIT_USER: { groupName: 'user', actionKey: 'canEdit' },
  DEACTIVATE_USER: { groupName: 'user', actionKey: 'canDeactivate' },
  ASSIGN_USER_PERMISSION: { groupName: 'user', actionKey: 'canAssignPermission' },
  ASSIGN_ADMINISTRATOR: { groupName: 'user', actionKey: 'canAssignAdmin' },
  APPROVE_USER_REGISTRATION: { groupName: 'user', actionKey: 'canApprove' },
  DELETE_USER: { groupName: 'user', actionKey: 'canDelete' },
  RESET_USER_PASSWORD: { groupName: 'user', actionKey: 'canResetPassword' },

  // USER ACTIVITY AUDIT TABLE
  VIEW_USER_ACTIVITY_AUDIT: { groupName: 'userActivityAudit', actionKey: 'canView' },

  //USER GROUP
  DELETE_USER_GROUP: { groupName: 'userGroup', actionKey: 'canDelete' },
  EDIT_USER_GROUP: { groupName: 'userGroup', actionKey: 'canEdit' },
  VIEW_USER_GROUP: { groupName: 'userGroup', actionKey: 'canView' },

  VIEW_APPROVERS: { groupName: 'approvers', actionKey: 'canView' },
  EDIT_APPROVERS: { groupName: 'approvers', actionKey: 'canEdit' },

  // WORKFLOW
  DELETE_DATA_WORKFLOW: { groupName: 'dataWorkflow', actionKey: 'canDelete' },
  IMPORT_DATA: { groupName: 'dataWorkflow', actionKey: 'canImport' },
  EXPORT_DATA: { groupName: 'dataWorkflow', actionKey: 'canExport' },

  CREATE_ACCOUNT: { groupName: 'accountWorkflow', actionKey: 'canCreate' },
  VIEW_OWN_ACCOUNT: { groupName: 'accountWorkflow', actionKey: 'canView' },
  VIEW_OTHER_ACCOUNT: { groupName: 'accountWorkflow', actionKey: 'canViewOtherAccount' },
  EDIT_OWN_ACCOUNT: { groupName: 'accountWorkflow', actionKey: 'canEdit' },
  EDIT_OTHER_ACCOUNT: { groupName: 'accountWorkflow', actionKey: 'canEditOtherAccount' },
  LEAD_OWNER_ASSIGN_LEAD: { groupName: 'accountWorkflow', actionKey: 'canLeadOwnerAssignToOther' },
  MERGE_ACCOUNT: { groupName: 'accountWorkflow', actionKey: 'canMerge' },
  RESTORE_INVALID_LEADS: { groupName: 'accountWorkflow', actionKey: 'canRestoreInvalidLeads' },
  RESTORE_LOST_LEADS: { groupName: 'accountWorkflow', actionKey: 'canRestoreLostLeads' },
  RESTORE_DELETED_LEADS: { groupName: 'accountWorkflow', actionKey: 'canRestoreDeletedLeads' },
  SKIP_MEETING_MINUTE_EMAIL: { groupName: 'accountWorkflow', actionKey: 'canSkipMeetingMinuteEmail' },
  EDIT_MEETING_HISTORY_LOCATION: { groupName: 'accountWorkflow', actionKey: 'canEditMeetingHistoryLocation' },
  VIEW_UNASSIGNED_LEAD: { groupName: 'accountWorkflow', actionKey: 'canViewUnassignedLead' },
  EDIT_UNASSIGNED_LEAD: { groupName: 'accountWorkflow', actionKey: 'canEditUnassignedLead' },
  RECORD_MODIFICATION: { groupName: 'accountWorkflow', actionKey: 'canEditConvertedLead' },
  ASSIGN_MEETING_OTHER_USER: { groupName: 'accountWorkflow', actionKey: 'canAssignMeetingOtherUser' },
  MANUAL_LEAD_STAGE_UPDATE: { groupName: 'accountWorkflow', actionKey: 'canManualLeadStageUpdate' },
  MANUAL_CLOSE_LEAD: { groupName: 'accountWorkflow', actionKey: 'canManualCloseLead' },
  EDIT_ACCOUNT: { groupName: 'accountWorkflow', actionKey: 'canEditAccount' },
  EDIT_CALL_LOG: { groupName: 'accountWorkflow', actionKey: 'canEditCallLog' },
  DELETE_CALL_LOG: { groupName: 'accountWorkflow', actionKey: 'canDeleteCallLog' },
  EDIT_LIVE_CALL: { groupName: 'accountWorkflow', actionKey: 'canEditLiveCall' },
  DELETE_ACCOUNT: { groupName: 'accountWorkflow', actionKey: 'canDeleteAccount' },
  EXPORT_LEAD_CONTACTS: { groupName: 'accountWorkflow', actionKey: 'canExportLeadContacts' },
  EXPORT_LEAD_ADDRESSES: { groupName: 'accountWorkflow', actionKey: 'canExportLeadAddresses' },
  EXPORT_LEAD_ACTIVITIES: { groupName: 'accountWorkflow', actionKey: 'canExportLeadActivities' },
  UNASSIGNED_LEAD_ASSIGN_OWNER: { groupName: 'accountWorkflow', actionKey: 'canAssignUnassignedLeadToOther' },
  LEAD_OWNER_TEAM_ASSIGN_LEAD: { groupName: 'accountWorkflow', actionKey: 'canLeadOwnerTeamAssignToOther' },
  LEAD_COLLABORATOR_ASSIGN_LEAD: { groupName: 'accountWorkflow', actionKey: 'canLeadCollaboratorAssignToOther' },
  VIEW_DELETED_LEADS: { groupName: 'accountWorkflow', actionKey: 'canViewDeletedLeads' },
  EDIT_LOST_INVALID_LEADS: { groupName: 'accountWorkflow', actionKey: 'canEditLostInvalidLeads' },
  EDIT_MY_TEAMS_LEADS: { groupName: 'accountWorkflow', actionKey: 'canEditTeam' },
  VIEW_MY_TEAMS_LEADS: { groupName: 'accountWorkflow', actionKey: 'canViewTeam' },

  DELETE_ACTIVITY: { groupName: 'accountActivity', actionKey: 'canDelete' },
  EXPORT_OPPORTUNITY_DATA: { groupName: 'dataWorkflow', actionKey: 'canExportOpportunity' },

  // CALENDAR
  VIEW_OWN_CALENDAR: { groupName: 'calendar', actionKey: 'canView' },
  VIEW_TEAM_CALENDAR: { groupName: 'calendar', actionKey: 'canViewTeam' },

  // PRODUCTS
  ADD_PRODUCT: { groupName: 'product', actionKey: 'canCreate' },
  VIEW_PRODUCT: { groupName: 'product', actionKey: 'canView' },
  EDIT_PRODUCT: { groupName: 'product', actionKey: 'canEdit' },
  DELETE_PRODUCT: { groupName: 'product', actionKey: 'canDelete' },

  CREATE_PRICE_TABLE: { groupName: 'price', actionKey: 'canCreate' },
  VIEW_PRICE_TABLE: { groupName: 'price', actionKey: 'canView' },
  EDIT_PRICE_TABLE: { groupName: 'price', actionKey: 'canEdit' },
  DEACTIVATE_PRICE_TABLE: { groupName: 'price', actionKey: 'canDeactivate' },
  DELETE_PRICE_TABLE: { groupName: 'price', actionKey: 'canDelete' },

  // SALES
  ADD_LEAD_SOURCE_TYPE: { groupName: 'leadSourceType', actionKey: 'canCreate' },
  VIEW_LEAD_SOURCE_TYPE: { groupName: 'leadSourceType', actionKey: 'canView' },
  EDIT_LEAD_SOURCE_TYPE: { groupName: 'leadSourceType', actionKey: 'canEdit' },

  ADD_LEAD_SOURCE: { groupName: 'leadSource', actionKey: 'canCreate' },
  VIEW_LEAD_SOURCE: { groupName: 'leadSource', actionKey: 'canView' },
  EDIT_LEAD_SOURCE: { groupName: 'leadSource', actionKey: 'canEdit' },
  DELETE_LEAD_SOURCE: { groupName: 'leadSource', actionKey: 'canDelete' },

  ADD_LEAD_CAMPAIGN: { groupName: 'leadCampaign', actionKey: 'canCreate' },
  VIEW_LEAD_CAMPAIGN: { groupName: 'leadCampaign', actionKey: 'canView' },
  EDIT_LEAD_CAMPAIGN: { groupName: 'leadCampaign', actionKey: 'canEdit' },
  DELETE_LEAD_CAMPAIGN: { groupName: 'leadCampaign', actionKey: 'canDelete' },
  DEACTIVATE_LEAD_CAMPAIGN: { groupName: 'leadCampaign', actionKey: 'canDeactivate' },

  // WORKFLOW MANAGEMENT
  CUSTOMIZE_LEAD_TYPE: { groupName: 'customizeLeadType', actionKey: 'canView' },
  CUSTOMIZE_LOOKUP_LISTS: { groupName: 'customizeLookupList', actionKey: 'canView' },
  CUSTOMIZE_FORM_DATA: { groupName: 'customizeFormData', actionKey: 'canView' },
  VIEW_LEAD_STATUSES: { groupName: 'leadStatuses', actionKey: 'canView' },
  EDIT_LEAD_STATUSES: { groupName: 'leadStatuses', actionKey: 'canEdit' },
  DELETE_LEAD_STATUSES: { groupName: 'leadStatuses', actionKey: 'canDelete' },
  VIEW_SALES_PIPELINES: { groupName: 'salesPipelines', actionKey: 'canView' },
  EDIT_SALES_PIPELINES: { groupName: 'salesPipelines', actionKey: 'canEdit' },
  DELETE_SALES_PIPELINES: { groupName: 'salesPipelines', actionKey: 'canDelete' },

  // EXTERNAL API
  GENERATE_EXTERNAL_API_TOKEN: { groupName: 'externalApi', actionKey: 'canCreate' },
  VIEW_EXTERNAL_API: { groupName: 'externalApi', actionKey: 'canView' },
  VIEW_PITCH_LIBRARY: { groupName: 'pitchLibrary', actionKey: 'canView' },
  EDIT_PITCH_LIBRARY: { groupName: 'pitchLibrary', actionKey: 'canEdit' },
  EDIT_EXTERNAL_API: { groupName: 'externalApi', actionKey: 'canEdit' },

  // PERMISSIONS MANAGEMENT
  CREATE_PROFILE: { groupName: 'permissions', actionKey: 'canCreate' },
  VIEW_PROFILE_PERMISSIONS: { groupName: 'permissions', actionKey: 'canView' },
  EDIT_PROFILE_PERMISSIONS: { groupName: 'permissions', actionKey: 'canEdit' },
  DELETE_PROFILE: { groupName: 'permissions', actionKey: 'canDelete' },

  // BRANCH SETTINGS
  VIEW_BRANCH_SETTINGS: { groupName: 'branchSettings', actionKey: 'canView' },
  EDIT_BRANCH_SETTINGS: { groupName: 'branchSettings', actionKey: 'canEdit' },

  // REPORTS
  VIEW_REPORT: { groupName: 'reports', actionKey: 'canView' },

  // CAMS
  IMPORT_FITMENTS: { groupName: 'cams', actionKey: 'canImport' },

  //AUTOMATION
  FULL_ACCESS_AUTOMATION: { groupName: 'automation', actionKey: 'canAnything' },
  READ_ONLY_AUTOMATION: { groupName: 'automation', actionKey: 'canView' },

  // PRICE
  APPROVED_PRICE: { groupName: 'price', actionKey: 'canApprove' },
  ALLOW_ESCALATION: { groupName: 'priceEscalation', actionKey: 'canEdit' },
  ALLOW_DISCOUNT_CHANGE: { groupName: 'priceDiscount', actionKey: 'canEdit' },

  // ACCOUNT
  UPDATE_LEAD_SOURCE: { groupName: 'account', actionKey: 'canUpdateLeadSource' },
  UPDATE_LEAD_CAMPAIGN: { groupName: 'account', actionKey: 'canUpdateLeadCampaign' },

  // OPPORTUNITY
  CREATE_OPPORTUNITY: { groupName: 'opportunity', actionKey: 'canCreateOpportunity' },

  //MANAGE CALLS
  EDIT_CALLBACK_TASK_TOGGLE_DEFAULT_STATE: {
    groupName: 'callBackTasks',
    actionKey: 'canEditCallBackTaskToggleDefaultState',
  },
  EDIT_CALLBACK_TASK_DEFAULT_ASSIGNEE: {
    groupName: 'callBackTasks',
    actionKey: 'canEditCallBackTaskDefaultAssignee',
  },
  EDIT_CALLBACK_TASK_DEFAULT_DATE_TIME: {
    groupName: 'callBackTasks',
    actionKey: 'canEditCallBackTaskDefaultDateTime',
  },
  EDIT_CALL_TASK_ASSIGNEE: {
    groupName: 'editCallTasks',
    actionKey: 'canEditCallTaskAssignee',
  },
  EDIT_CALL_TASK_DUE_DATE_TIME: {
    groupName: 'editCallTasks',
    actionKey: 'canEditCallTaskDueDateTime',
  },
  DELETE_CALL_TASK: {
    groupName: 'editCallTasks',
    actionKey: 'canDelete',
  },
  EDIT_CALLBACK_ROUTING: {
    groupName: 'editCallTasks',
    actionKey: 'canEditCallbackRouting',
  },
  EDIT_MESSAGE_LOG: { groupName: 'managingMessages', actionKey: 'canEditMessageLog' },
  DELETE_MESSAGE_LOG: { groupName: 'managingMessages', actionKey: 'canDeleteMessageLog' },

  //LEAD EMAIL LOG
  EDIT_LEAD_EMAIL_LOG: { groupName: 'leadEmailLog', actionKey: 'canEditLeadEmailLog' },
  DELETE_LEAD_EMAIL_LOG: { groupName: 'leadEmailLog', actionKey: 'canDeleteLeadEmailLog' },

  // LEAD CUSTOM LAYOUT PERMISSIONS
  VIEW_LEAD_DETAIL_PAGE_LAYOUTS: {
    groupName: 'leadDetailPageCustomization',
    actionKey: 'canViewCustomLeadDetailLayout',
  },
  ASSIGN_LEAD_DETAIL_PAGE_LAYOUTS: {
    groupName: 'leadDetailPageCustomization',
    actionKey: 'canAssignCustomLeadDetailLayout',
  },
  CREATE_AND_EDIT_LEAD_DETAIL_PAGE_LAYOUTS: {
    groupName: 'leadDetailPageCustomization',
    actionKey: 'canEditAndCreateCustomLeadDetailLayout',
  },
  DELETE_LEAD_DETAIL_PAGE_LAYOUTS: {
    groupName: 'leadDetailPageCustomization',
    actionKey: 'canDeleteCustomLeadDetailLayout',
  },
  PERSONALISE_MY_LEAD_DETAIL_PAGE_LAYOUT: {
    groupName: 'leadDetailPageCustomization',
    actionKey: 'canPersonalizeLeadDetailPageLayout',
  },
}
